import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { getApiBase } from "../apiBase";
import { isCvinExpansion, formatExpansionWorkOrderForDisplay } from "../projectGridColumns";
import ProjectFieldFileControls from "../ProjectFieldFileControls";
import { CommercialFieldFileIcon } from "./CommercialFieldFileDisplay";
import { COMMERCIAL_DOMAINS } from "./commercialProjectSchema";
import { saveProjectPermitCoverage } from "./permitCoverageApi";
import "./permitCoveragePanel.css";

function HostProjectPicker({ projects, selectedId, excludeId, onChange, disabled }) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...projects]
      .filter((row) => Number(row.id) !== Number(excludeId))
      .sort((a, b) =>
        String(a.work_order || "").localeCompare(String(b.work_order || ""), undefined, {
          numeric: true,
        })
      );
    if (!q) return list.slice(0, 200);
    return list
      .filter((row) => {
        const hay = `${row.work_order || ""} ${row.name || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 200);
  }, [projects, query, excludeId]);

  return (
    <div className="permit-coverage-picker">
      <div className="permit-coverage-picker-search">
        <input
          type="search"
          className="commercial-field-input"
          placeholder="Search expansion projects by work order…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
        />
      </div>
      {filtered.length === 0 ? (
        <p className="project-detail-muted permit-coverage-picker-empty">No projects match.</p>
      ) : (
        filtered.map((row) => (
          <label key={row.id} className="permit-coverage-picker-row">
            <input
              type="radio"
              name="permit-coverage-host"
              checked={Number(selectedId) === Number(row.id)}
              onChange={() => onChange(row.id)}
              disabled={disabled}
            />
            <span>
              <strong>{formatExpansionWorkOrderForDisplay(row) || `#${row.id}`}</strong>
              {row.name ? (
                <span className="permit-coverage-picker-meta"> — {row.name}</span>
              ) : null}
            </span>
          </label>
        ))
      )}
    </div>
  );
}

/**
 * Expansion Permitting tab: own application vs covered by another project.
 * Also shown view-only on Engineering and Construction tabs.
 */
export default function PermitCoveragePanel({
  projectId,
  coverage,
  onCoverageChange,
  editing = false,
  apiUrl,
  getAuthHeaders,
  confirmationValue,
  onConfirmationFileSynced,
}) {
  const [hostCandidates, setHostCandidates] = useState([]);
  const [draftHostId, setDraftHostId] = useState(null);
  const [draftNotes, setDraftNotes] = useState("");
  const [mode, setMode] = useState("own");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!coverage) {
      setMode("own");
      setDraftHostId(null);
      setDraftNotes("");
      return;
    }
    setMode(coverage.mode === "hosted_by" ? "hosted_by" : "own");
    setDraftHostId(coverage.hostProjectId ?? coverage.hostProject?.id ?? null);
    setDraftNotes(coverage.notes || "");
  }, [coverage]);

  useEffect(() => {
    if (!editing || !projectId) return undefined;
    let cancelled = false;
    const base = apiUrl || getApiBase();
    axios
      .get(`${base}/projects`, { headers: getAuthHeaders?.() })
      .then((res) => {
        if (cancelled) return;
        const rows = (res.data || []).filter((r) => isCvinExpansion(r));
        setHostCandidates(rows);
      })
      .catch(() => {
        if (!cancelled) setHostCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [editing, projectId, apiUrl, getAuthHeaders]);

  const coveredProjects = coverage?.coveredProjects || [];
  const hostProject = coverage?.hostProject || null;
  const isHosted = mode === "hosted_by";

  const persist = useCallback(
    async ({ hostProjectId, notes }) => {
      if (!projectId || !editing) return;
      setSaving(true);
      setError("");
      try {
        const result = await saveProjectPermitCoverage(
          projectId,
          { hostProjectId, notes },
          { apiUrl, getAuthHeaders }
        );
        onCoverageChange?.(result);
      } catch (err) {
        setError(err.response?.data?.error || err.message || "Could not save permit coverage.");
      } finally {
        setSaving(false);
      }
    },
    [apiUrl, editing, getAuthHeaders, onCoverageChange, projectId]
  );

  const onSelectOwn = () => {
    setMode("own");
    setDraftHostId(null);
    persist({ hostProjectId: null, notes: draftNotes });
  };

  const onSelectHosted = () => {
    setMode("hosted_by");
  };

  const onPickHost = (id) => {
    setDraftHostId(id);
    persist({ hostProjectId: id, notes: draftNotes });
  };

  const onBlurNotes = () => {
    if (!editing) return;
    const savedNotes = coverage?.notes || "";
    if (String(draftNotes) === String(savedNotes)) return;
    if (mode === "hosted_by" && draftHostId) {
      persist({ hostProjectId: draftHostId, notes: draftNotes });
    }
  };

  const fileEditing =
    editing && projectId && getAuthHeaders
      ? {
          projectId,
          getAuthHeaders,
          onFileUpdated: (result) => onConfirmationFileSynced?.(result),
        }
      : null;

  return (
    <section className="commercial-section-panel permit-coverage-panel">
      <h3 className="commercial-section-title">Permit coverage</h3>
      <p className="project-detail-muted permit-coverage-lead">
        Use this when another expansion project holds the agency application that covers this work
        order (for example permitting under a different segment).
      </p>

      {error ? <p className="project-detail-error">{error}</p> : null}

      {editing ? (
        <div className="permit-coverage-mode">
          <label className="permit-coverage-mode-option">
            <input
              type="radio"
              name="permit-coverage-mode"
              checked={mode === "own"}
              onChange={onSelectOwn}
              disabled={saving}
            />
            Own permit application
          </label>
          <label className="permit-coverage-mode-option">
            <input
              type="radio"
              name="permit-coverage-mode"
              checked={mode === "hosted_by"}
              onChange={onSelectHosted}
              disabled={saving}
            />
            Covered by another project
          </label>
        </div>
      ) : (
        <p className="permit-coverage-mode-readonly">
          {isHosted ? "Covered by another project" : "Own permit application"}
        </p>
      )}

      {isHosted ? (
        <div className="permit-coverage-hosted">
          {editing ? (
            <>
              <div className="commercial-field-row">
                <label className="commercial-field-label">
                  Host project
                  <span
                    className="project-detail-muted"
                    style={{ display: "block", fontWeight: 400, fontSize: 12 }}
                  >
                    Expansion project that has the agency application.
                  </span>
                </label>
                <div className="commercial-field-value">
                  <HostProjectPicker
                    projects={hostCandidates}
                    selectedId={draftHostId}
                    excludeId={projectId}
                    onChange={onPickHost}
                    disabled={saving}
                  />
                </div>
              </div>
              <div className="commercial-field-row">
                <label className="commercial-field-label">Coverage notes</label>
                <div className="commercial-field-value">
                  <textarea
                    className="commercial-field-textarea"
                    rows={2}
                    value={draftNotes}
                    onChange={(e) => setDraftNotes(e.target.value)}
                    onBlur={onBlurNotes}
                    disabled={saving || !draftHostId}
                    placeholder="Optional note (e.g. agency email confirmation)"
                  />
                </div>
              </div>
            </>
          ) : hostProject ? (
            <p className="permit-coverage-host-readonly">
              Host:{" "}
              <Link to={`/project/${hostProject.id}`} className="interconnects-link">
                {hostProject.workOrder || `#${hostProject.id}`}
              </Link>
              {hostProject.name ? ` — ${hostProject.name}` : ""}
              {coverage?.notes ? (
                <span className="project-detail-muted"> · {coverage.notes}</span>
              ) : null}
            </p>
          ) : (
            <p className="project-detail-muted">No host project selected.</p>
          )}

          <div className="commercial-field-row">
            <label className="commercial-field-label">
              Confirmation
              <span
                className="project-detail-muted"
                style={{ display: "block", fontWeight: 400, fontSize: 12 }}
              >
                Agency email or rider confirmation (optional).
              </span>
            </label>
            <div className="commercial-field-value">
              {fileEditing ? (
                <ProjectFieldFileControls
                  projectId={fileEditing.projectId}
                  fieldPath={[COMMERCIAL_DOMAINS.ENGINEERING, "permit_coverage_confirmation"]}
                  value={confirmationValue}
                  getAuthHeaders={fileEditing.getAuthHeaders}
                  onUpdated={(result) => fileEditing.onFileUpdated?.(result)}
                  accept=".pdf,.png,.jpeg,.jpg,.eml,.msg"
                />
              ) : (
                <CommercialFieldFileIcon
                  value={confirmationValue}
                  fieldKey="permit_coverage_confirmation"
                  accept=".pdf,.png,.jpeg,.jpg,.eml,.msg"
                />
              )}
            </div>
          </div>

          {editing && draftHostId ? (
            <p className="project-detail-muted permit-coverage-hint">
              The agency application lives on the host project. You can still keep local permit rows
              below if needed.
            </p>
          ) : null}
        </div>
      ) : null}

      {coveredProjects.length > 0 ? (
        <div className="permit-coverage-also">
          <h4 className="permit-coverage-also-title">Also covers</h4>
          <ul className="permit-coverage-also-list">
            {coveredProjects.map((row) => (
              <li key={row.id}>
                <Link to={`/project/${row.id}`} className="interconnects-link">
                  {row.workOrder || `#${row.id}`}
                </Link>
                {row.name ? <span className="permit-coverage-picker-meta"> — {row.name}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {saving ? <p className="project-detail-muted">Saving coverage…</p> : null}
    </section>
  );
}
